import { useState, useRef, useEffect } from "react";
import { useToast } from "Components/Common/Toast/ToastContext";
import {
  getProjectDocuments,
  downloadDocumentBlob,
  uploadProjectDocuments,
} from "services/projectService";
import { uploadFileToS3 } from "Pages/GcPrecon/GcPreconCommercial/UploadFileToS3";
import { triggerBlobDownload } from "utils/Common/fileDownload";

export function useDocuments(projectId: string | undefined, enabled: boolean) {
  const showToast = useToast();
  const fetchedRef = useRef(false);
  const [documents, setDocuments] = useState<Record<string, any[]> | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchDocuments = (id: string) => {
    setLoading(true);
    (getProjectDocuments(id) as Promise<{ data: any; error: any }>).then(
      ({ data, error }) => {
        if (!error && data) setDocuments(data?.data ?? null);
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    if (!enabled || !projectId || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchDocuments(projectId);
  }, [enabled, projectId]);

  const handleDownload = async (doc: any, useOwnerId = false) => {
    if (!projectId) return;
    setDownloadingId(doc.id);
    const blob = await downloadDocumentBlob(projectId, doc.id, useOwnerId);
    if (blob)
      triggerBlobDownload(blob, doc.label ?? doc.value?.fileName ?? "document");
    else showToast("Document not found.");
    setDownloadingId(null);
  };

  const handleUpload = async (file: File, category: string) => {
    if (!projectId) return;
    setUploading(true);
    try {
      const { key } = await uploadFileToS3(file);
      const item = {
        category,
        label: file.name,
        value: { key, fileName: file.name },
      };
      const { error: uploadErr } = (await uploadProjectDocuments(projectId, [
        item,
      ])) as any;
      if (uploadErr) {
        showToast("Upload failed. Please try again.");
        return;
      }
      showToast("File uploaded successfully");
      fetchedRef.current = false;
      fetchDocuments(projectId);
    } catch {
      showToast("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return {
    documents,
    loading,
    uploading,
    downloadingId,
    handleDownload,
    handleUpload,
  };
}
